import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Stack,
  AppBar,
  Toolbar,
  Button,
  CircularProgress,
  Divider,
  Alert,
  Link as MuiLink,
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import {
  CheckCircle as CheckIcon,
  HourglassEmpty as PendingIcon,
  CloudDone as ProofIcon,
  AccountBalance as ReceivedIcon,
  Verified as ReconciledIcon,
  Undo as RefundedIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { useParams, Link } from 'react-router-dom';

// Premium color palette
const colors = {
  primaryDark: '#0D1B2A',
  primaryMid: '#1B263B',
  primaryLight: '#415A77',
  accent: '#FF6B35',
  accentLight: '#FF8C5A',
  teal: '#00B4D8',
  tealLight: '#48CAE4',
  white: '#FFFFFF',
  offWhite: '#F8F9FA',
  midGray: '#778DA9',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
};

interface PaymentData {
  invoiceId: string;
  clientName: string;
  clientEmail: string;
  clientCompany: string | null;
  amountUsd: number;
  description: string | null;
  dueDate: string | null;
  railPreference: 'USDC' | 'ACH' | 'WIRE';
  status: string;
  proofSubmittedAt: string | null;
  receivedAt: string | null;
  createdAt: string;
}

const statusConfig: Record<string, { label: string; color: string; icon: React.ReactNode; description: string }> = {
  draft: {
    label: 'Draft',
    color: colors.midGray,
    icon: <PendingIcon />,
    description: 'Payment request is being prepared.',
  },
  pending: {
    label: 'Pending Payment',
    color: colors.warning,
    icon: <PendingIcon />,
    description: 'Awaiting payment. Please send funds using the instructions provided.',
  },
  proof_submitted: {
    label: 'Proof Submitted',
    color: colors.teal,
    icon: <ProofIcon />,
    description: 'Payment proof received. We are verifying the transaction.',
  },
  received: {
    label: 'Payment Received',
    color: colors.success,
    icon: <ReceivedIcon />,
    description: 'Payment has been confirmed and received.',
  },
  reconciled: {
    label: 'Completed',
    color: colors.success,
    icon: <ReconciledIcon />,
    description: 'Payment has been fully processed and reconciled.',
  },
  refunded: {
    label: 'Refunded',
    color: colors.error,
    icon: <RefundedIcon />,
    description: 'This payment has been refunded.',
  },
};

const PaymentReceiptPage: React.FC = () => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [payment, setPayment] = useState<PaymentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (invoiceId) {
      fetchPayment();
    }
  }, [invoiceId]);

  const fetchPayment = async () => {
    try {
      const res = await fetch(`/api/payments/${invoiceId}`);
      const data = await res.json();

      if (data.success) {
        setPayment(data.data);
      } else {
        setError(data.error || 'Payment not found');
      }
    } catch (err) {
      setError('Failed to load payment details');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <Box sx={{ 
        minHeight: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: colors.primaryDark 
      }}>
        <CircularProgress sx={{ color: colors.teal }} />
      </Box>
    );
  }

  if (error || !payment) {
    return (
      <Box sx={{ 
        minHeight: '100vh',
        background: colors.primaryDark,
      }}>
        <AppBar position="static" elevation={0} sx={{ background: 'transparent' }}>
          <Container maxWidth="lg">
            <Toolbar>
              <Typography
                component="a"
                href="/landing.html"
                sx={{
                  fontWeight: 900,
                  fontSize: '1.5rem',
                  background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.accent} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textDecoration: 'none',
                }}
              >
                REDEX
              </Typography>
            </Toolbar>
          </Container>
        </AppBar>
        <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Payment not found'}
          </Alert>
          <Button
            component={Link}
            to="/payments"
            variant="contained"
            sx={{
              background: colors.teal,
              '&:hover': { background: colors.tealLight },
            }}
          >
            Go to Payments
          </Button>
        </Container>
      </Box>
    );
  }

  const statusInfo = statusConfig[payment.status] || statusConfig.pending;

  return (
    <Box sx={{ 
      minHeight: '100vh',
      background: `linear-gradient(180deg, ${colors.primaryDark} 0%, ${colors.primaryMid} 100%)`,
    }}>
      {/* Header */}
      <AppBar 
        position="static" 
        elevation={0}
        sx={{
          background: 'rgba(13, 27, 42, 0.9)',
          backdropFilter: 'blur(20px)',
          borderBottom: `1px solid rgba(255, 255, 255, 0.08)`,
        }}
      >
        <Container maxWidth="lg">
          <Toolbar sx={{ justifyContent: 'space-between', py: 1 }}>
            <Stack direction="row" spacing={3} alignItems="center">
              <Typography
                component="a"
                href="/landing.html"
                sx={{
                  fontWeight: 900,
                  fontSize: '1.5rem',
                  background: `linear-gradient(135deg, ${colors.teal} 0%, ${colors.accent} 100%)`,
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textDecoration: 'none',
                }}
              >
                REDEX
              </Typography>
              <Chip
                label="PAYMENT RECEIPT"
                size="small"
                sx={{
                  background: 'rgba(0, 180, 216, 0.15)',
                  color: colors.tealLight,
                  fontWeight: 600,
                  fontSize: '0.7rem',
                  letterSpacing: '0.05em',
                  border: `1px solid rgba(0, 180, 216, 0.3)`,
                }}
              />
            </Stack>
            <Button
              component={Link}
              to="/payments"
              startIcon={<ArrowBackIcon />}
              sx={{ 
                color: colors.midGray, 
                textTransform: 'none',
                '&:hover': { color: colors.white },
              }}
            >
              Back to Payments
            </Button>
          </Toolbar>
        </Container>
      </AppBar>

      <Container maxWidth="md" sx={{ py: 6 }}>
        {/* Status Banner */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            background: `rgba(${statusInfo.color === colors.success ? '16, 185, 129' : statusInfo.color === colors.warning ? '245, 158, 11' : statusInfo.color === colors.teal ? '0, 180, 216' : '239, 68, 68'}, 0.15)`,
            border: `1px solid ${statusInfo.color}`,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" mb={2}>
            <Box sx={{ color: statusInfo.color, '& svg': { fontSize: 40 } }}>
              {statusInfo.icon}
            </Box>
            <Typography variant="h4" sx={{ color: colors.white, fontWeight: 700 }}>
              {statusInfo.label}
            </Typography>
          </Stack>
          <Typography variant="body1" sx={{ color: colors.midGray }}>
            {statusInfo.description}
          </Typography>
        </Paper>

        {/* Invoice Details */}
        <Paper
          sx={{
            p: 4,
            mb: 4,
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(10px)',
            border: `1px solid rgba(255, 255, 255, 0.1)`,
            borderRadius: 3,
          }}
        >
          <Stack direction="row" spacing={2} alignItems="center" mb={3}>
            <ReceiptIcon sx={{ color: colors.teal, fontSize: 28 }} />
            <Typography variant="h5" sx={{ color: colors.white, fontWeight: 600 }}>
              Payment Details
            </Typography>
          </Stack>

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="caption" sx={{ color: colors.midGray }}>
                Invoice ID
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="h6"
                  sx={{ color: colors.accent, fontFamily: 'monospace', fontWeight: 600 }}
                >
                  {payment.invoiceId}
                </Typography>
                <Button 
                  size="small" 
                  onClick={() => copyToClipboard(payment.invoiceId)}
                  sx={{ minWidth: 'auto', p: 0.5, color: colors.midGray }}
                >
                  <CopyIcon fontSize="small" />
                </Button>
              </Stack>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: colors.midGray }}>
                Amount
              </Typography>
              <Typography variant="h6" sx={{ color: colors.white, fontWeight: 700 }}>
                ${payment.amountUsd.toLocaleString('en-US', { minimumFractionDigits: 2 })} USD
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: colors.midGray }}>
                Client
              </Typography>
              <Typography variant="body1" sx={{ color: colors.white }}>
                {payment.clientName}
                {payment.clientCompany && (
                  <Typography component="span" sx={{ color: colors.midGray, ml: 1 }}>
                    ({payment.clientCompany})
                  </Typography>
                )}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: colors.midGray }}>
                Email
              </Typography>
              <Typography variant="body1" sx={{ color: colors.white }}>
                {payment.clientEmail}
              </Typography>
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: colors.midGray }}>
                Payment Method
              </Typography>
              <Chip
                label={payment.railPreference}
                size="small"
                sx={{
                  background: payment.railPreference === 'USDC' 
                    ? 'rgba(0, 180, 216, 0.2)' 
                    : 'rgba(255, 255, 255, 0.1)',
                  color: payment.railPreference === 'USDC' ? colors.teal : colors.white,
                  fontWeight: 600,
                }}
              />
            </Box>

            <Box>
              <Typography variant="caption" sx={{ color: colors.midGray }}>
                Created
              </Typography>
              <Typography variant="body1" sx={{ color: colors.white }}>
                {formatDate(payment.createdAt)}
              </Typography>
            </Box>

            {payment.description && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <Typography variant="caption" sx={{ color: colors.midGray }}>
                  Description
                </Typography>
                <Typography variant="body1" sx={{ color: colors.white }}>
                  {payment.description}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Timeline */}
        <Paper
          sx={{
            p: 4,
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(10px)',
            border: `1px solid rgba(255, 255, 255, 0.1)`,
            borderRadius: 3,
          }}
        >
          <Typography variant="h6" sx={{ color: colors.white, mb: 3 }}>
            Payment Timeline
          </Typography>

          <Timeline position="alternate">
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot sx={{ background: colors.success }}>
                  <CheckIcon fontSize="small" />
                </TimelineDot>
                <TimelineConnector sx={{ background: 'rgba(255,255,255,0.2)' }} />
              </TimelineSeparator>
              <TimelineContent>
                <Typography variant="subtitle2" sx={{ color: colors.white }}>
                  Request Created
                </Typography>
                <Typography variant="caption" sx={{ color: colors.midGray }}>
                  {formatDate(payment.createdAt)}
                </Typography>
              </TimelineContent>
            </TimelineItem>

            {payment.proofSubmittedAt && (
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot sx={{ background: colors.teal }}>
                    <ProofIcon fontSize="small" />
                  </TimelineDot>
                  <TimelineConnector sx={{ background: 'rgba(255,255,255,0.2)' }} />
                </TimelineSeparator>
                <TimelineContent>
                  <Typography variant="subtitle2" sx={{ color: colors.white }}>
                    Proof Submitted
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.midGray }}>
                    {formatDate(payment.proofSubmittedAt)}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            )}

            {payment.receivedAt && (
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot sx={{ background: colors.success }}>
                    <ReceivedIcon fontSize="small" />
                  </TimelineDot>
                  {payment.status !== 'reconciled' && (
                    <TimelineConnector sx={{ background: 'rgba(255,255,255,0.2)' }} />
                  )}
                </TimelineSeparator>
                <TimelineContent>
                  <Typography variant="subtitle2" sx={{ color: colors.white }}>
                    Payment Received
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.midGray }}>
                    {formatDate(payment.receivedAt)}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            )}

            {!payment.proofSubmittedAt && payment.status === 'pending' && (
              <TimelineItem>
                <TimelineSeparator>
                  <TimelineDot sx={{ background: 'rgba(255,255,255,0.3)' }}>
                    <PendingIcon fontSize="small" />
                  </TimelineDot>
                </TimelineSeparator>
                <TimelineContent>
                  <Typography variant="subtitle2" sx={{ color: colors.midGray }}>
                    Awaiting Payment
                  </Typography>
                  <Typography variant="caption" sx={{ color: colors.midGray }}>
                    Submit proof after payment
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            )}
          </Timeline>
        </Paper>

        {/* Action Buttons */}
        {payment.status === 'pending' && (
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              component={Link}
              to="/payments"
              variant="contained"
              size="large"
              sx={{
                px: 4,
                py: 1.5,
                background: `linear-gradient(135deg, ${colors.accent} 0%, ${colors.accentLight} 100%)`,
                fontWeight: 600,
                '&:hover': {
                  boxShadow: `0 6px 30px rgba(255, 107, 53, 0.3)`,
                },
              }}
            >
              Submit Proof of Payment
            </Button>
          </Box>
        )}

        {/* Support */}
        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: colors.midGray }}>
            Questions about this payment?{' '}
            <MuiLink href="mailto:payments@redexct.com" sx={{ color: colors.teal }}>
              Contact Support
            </MuiLink>
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

export default PaymentReceiptPage;
